import React, { useEffect, useState } from 'react';
import { SaveIcon, InfoIcon, ClockIcon, UploadCloudIcon, GlobeIcon } from 'lucide-react';
export function CountryDetail({
  country,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    alpha2Code: '',
    alpha3Code: '',
    numericCode: '',
    commonName: '',
    officialName: '',
    officialCurrency: '',
    phoneCode: '',
    region: '',
    subRegion: '',
    customFields: {}
  });
  // Sample history data - in real app, this would come from API
  const [history, setHistory] = useState([{
    id: 1,
    eventType: 'updated',
    eventDescription: 'Official name updated',
    timestamp: '2026-01-19T14:30:00Z',
    userId: 'user-123',
    userName: 'John Smith',
    changes: [{
      field: 'officialName',
      oldValue: 'Canada',
      newValue: 'Canada'
    }]
  }, {
    id: 2,
    eventType: 'updated',
    eventDescription: 'Phone code updated',
    timestamp: '2026-01-15T09:15:00Z',
    userId: 'user-456',
    userName: 'Jane Doe',
    changes: [{
      field: 'phoneCode',
      oldValue: '+1',
      newValue: '+1'
    }]
  }, {
    id: 3,
    eventType: 'created',
    eventDescription: 'Country created',
    timestamp: '2026-01-10T16:45:00Z',
    userId: 'user-789',
    userName: 'Admin User',
    changes: []
  }]);
  // Available regions and subregions for dropdown selection
  const regions = ['Africa', 'Americas', 'Asia', 'Europe', 'Oceania'];
  const subRegionsByRegion = {
    Africa: ['Northern Africa', 'Eastern Africa', 'Middle Africa', 'Southern Africa', 'Western Africa'],
    Americas: ['Northern America', 'Caribbean', 'Central America', 'South America'],
    Asia: ['Central Asia', 'Eastern Asia', 'Southern Asia', 'South-Eastern Asia', 'Western Asia'],
    Europe: ['Eastern Europe', 'Northern Europe', 'Southern Europe', 'Western Europe'],
    Oceania: ['Australia and New Zealand', 'Melanesia', 'Micronesia', 'Polynesia']
  };
  // Common currencies for quick selection
  const commonCurrencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'CHF',
    name: 'Swiss Franc'
  }, {
    code: 'CNY',
    name: 'Chinese Yuan'
  }, {
    code: 'INR',
    name: 'Indian Rupee'
  }, {
    code: 'SGD',
    name: 'Singapore Dollar'
  }];
  useEffect(() => {
    if (country && !isCreating) {
      setFormData({
        ...country,
        // Handle legacy data that might only have 'name' field
        commonName: country.commonName || country.name || '',
        officialName: country.officialName || '',
        customFields: {
          ...country.customFields
        }
      });
    } else {
      // Default values for new country
      setFormData({
        alpha2Code: '',
        alpha3Code: '',
        numericCode: '',
        commonName: '',
        officialName: '',
        officialCurrency: '',
        phoneCode: '',
        region: '',
        subRegion: '',
        customFields: {}
      });
    }
  }, [country, isCreating]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleRegionChange = e => {
    const newRegion = e.target.value;
    setFormData({
      ...formData,
      region: newRegion,
      // Reset subregion when region changes
      subRegion: ''
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  const getEventIcon = eventType => {
    switch (eventType) {
      case 'created':
        return <UploadCloudIcon className="h-5 w-5" data-id="element-17074" />;
      case 'updated':
        return <InfoIcon className="h-5 w-5" data-id="element-17075" />;
      default:
        return <InfoIcon className="h-5 w-5" data-id="element-17076" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17077">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-17078">
        <div data-id="element-17079">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17080">
            {isCreating ? 'Add New Country' : `${formData.commonName || formData.name} (${formData.alpha2Code})`}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17081">
            {isCreating ? 'Enter details for the new country' : 'View and update country details'}
          </p>
        </div>
        {activeTab === 'details' && <div className="flex space-x-3" data-id="element-17082">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17083">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17084">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-17085" />
              Save Country
            </button>
          </div>}
      </div>

      {/* Tabs */}
      {!isCreating && <div className="border-b border-gray-200" data-id="element-17086">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-17087">
            <button onClick={() => setActiveTab('details')} className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17088">
              Details
            </button>
            <button onClick={() => setActiveTab('relationships')} className={`${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17089">
              Relationships
            </button>
            <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-17090">
              History
            </button>
          </nav>
        </div>}

      {/* Tab Content */}
      <div className="px-4 py-5 sm:px-6" data-id="element-17091">
        {/* Details Tab */}
        {activeTab === 'details' && <form className="space-y-6" onSubmit={handleSubmit} data-id="element-17092">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17093">
              {/* Basic Information */}
              <div className="sm:col-span-6" data-id="element-17094">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-17095">
                  Basic Information
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-17096">
                <label htmlFor="commonName" className="block text-sm font-medium text-gray-700" data-id="element-17097">
                  Common Name <span className="text-red-500" data-id="element-17098">*</span>
                </label>
                <div className="mt-1" data-id="element-17099">
                  <input type="text" name="commonName" id="commonName" value={formData.commonName} onChange={handleChange} placeholder="e.g., Sri Lanka" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17100" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-17101">
                  The commonly used name for the country
                </p>
              </div>

              <div className="sm:col-span-3" data-id="element-17102">
                <label htmlFor="officialName" className="block text-sm font-medium text-gray-700" data-id="element-17103">
                  Official Name
                </label>
                <div className="mt-1" data-id="element-17104">
                  <input type="text" name="officialName" id="officialName" value={formData.officialName} onChange={handleChange} placeholder="e.g., Democratic Socialist Republic of Sri Lanka" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17105" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-17106">
                  The official constitutional name (optional)
                </p>
              </div>

              {/* Info banner about name usage */}
              <div className="sm:col-span-6" data-id="element-17107">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3" data-id="element-17108">
                  <div className="flex" data-id="element-17109">
                    <div className="flex-shrink-0" data-id="element-17110">
                      <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-17111" />
                    </div>
                    <div className="ml-3" data-id="element-17112">
                      <p className="text-sm text-blue-700" data-id="element-17113">
                        The <strong data-id="element-17114">common name</strong> will be used throughout
                        the application for display purposes. The{' '}
                        <strong data-id="element-17115">official name</strong> can be used for formal
                        documents and reports.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-17116">
                <label htmlFor="phoneCode" className="block text-sm font-medium text-gray-700" data-id="element-17117">
                  Phone Code
                </label>
                <div className="mt-1" data-id="element-17118">
                  <input type="text" name="phoneCode" id="phoneCode" value={formData.phoneCode} onChange={handleChange} placeholder="+XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17119" />
                </div>
              </div>

              {/* ISO Codes */}
              <div className="sm:col-span-6" data-id="element-17120">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-17121">
                  ISO Codes
                </h4>
              </div>

              <div className="sm:col-span-2" data-id="element-17122">
                <label htmlFor="alpha2Code" className="block text-sm font-medium text-gray-700" data-id="element-17123">
                  Alpha-2 Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-17124">*</span>
                </label>
                <div className="mt-1" data-id="element-17125">
                  <input type="text" name="alpha2Code" id="alpha2Code" value={formData.alpha2Code} onChange={handleChange} maxLength={2} placeholder="XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} data-id="element-17126" />
                </div>
                {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-17127">
                    Alpha-2 code cannot be changed after creation
                  </p>}
              </div>

              <div className="sm:col-span-2" data-id="element-17128">
                <label htmlFor="alpha3Code" className="block text-sm font-medium text-gray-700" data-id="element-17129">
                  Alpha-3 Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-17130">*</span>
                </label>
                <div className="mt-1" data-id="element-17131">
                  <input type="text" name="alpha3Code" id="alpha3Code" value={formData.alpha3Code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-17132" />
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-17133">
                <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-17134">
                  Numeric Code (ISO 3166-1){' '}
                  <span className="text-red-500" data-id="element-17135">*</span>
                </label>
                <div className="mt-1" data-id="element-17136">
                  <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17137" />
                </div>
              </div>

              {/* Geography */}
              <div className="sm:col-span-6" data-id="element-17138">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-17139">
                  Geography
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-17140">
                <label htmlFor="region" className="block text-sm font-medium text-gray-700" data-id="element-17141">
                  Region <span className="text-red-500" data-id="element-17142">*</span>
                </label>
                <div className="mt-1" data-id="element-17143">
                  <select id="region" name="region" value={formData.region} onChange={handleRegionChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17144">
                    <option value="" data-id="element-17145">Select a region</option>
                    {regions.map(region => <option key={region} value={region} data-id="element-17146">
                        {region}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-17147">
                <label htmlFor="subRegion" className="block text-sm font-medium text-gray-700" data-id="element-17148">
                  Sub-Region <span className="text-red-500" data-id="element-17149">*</span>
                </label>
                <div className="mt-1" data-id="element-17150">
                  <select id="subRegion" name="subRegion" value={formData.subRegion} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" disabled={!formData.region} required data-id="element-17151">
                    <option value="" data-id="element-17152">Select a sub-region</option>
                    {formData.region && subRegionsByRegion[formData.region]?.map(subRegion => <option key={subRegion} value={subRegion} data-id="element-17153">
                          {subRegion}
                        </option>)}
                  </select>
                </div>
              </div>

              {/* Currency */}
              <div className="sm:col-span-6" data-id="element-17154">
                <h4 className="text-sm font-medium text-gray-900 mb-3 mt-4" data-id="element-17155">
                  Currency
                </h4>
              </div>

              <div className="sm:col-span-3" data-id="element-17156">
                <label htmlFor="officialCurrency" className="block text-sm font-medium text-gray-700" data-id="element-17157">
                  Official Currency <span className="text-red-500" data-id="element-17158">*</span>
                </label>
                <div className="mt-1" data-id="element-17159">
                  <select id="officialCurrency" name="officialCurrency" value={formData.officialCurrency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-17160">
                    <option value="" data-id="element-17161">Select a currency</option>
                    {commonCurrencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-17162">
                        {currency.code} - {currency.name}
                      </option>)}
                    <option value="OTHER" data-id="element-17163">Other (enter manually)</option>
                  </select>
                </div>
              </div>

              {formData.officialCurrency === 'OTHER' && <div className="sm:col-span-3" data-id="element-17164">
                  <label htmlFor="customCurrency" className="block text-sm font-medium text-gray-700" data-id="element-17165">
                    Custom Currency Code
                  </label>
                  <div className="mt-1" data-id="element-17166">
                    <input type="text" name="officialCurrency" id="customCurrency" value={formData.officialCurrency === 'OTHER' ? '' : formData.officialCurrency} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-17167" />
                  </div>
                </div>}
            </div>

            <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200" data-id="element-17168">
              <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17169">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17170">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-17171" />
                {isCreating ? 'Create Country' : 'Update Country'}
              </button>
            </div>
          </form>}

        {/* Relationships Tab */}
        {activeTab === 'relationships' && <div className="py-4" data-id="element-17172">
            <div className="text-center py-12" data-id="element-17173">
              <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17174" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17175">
                No relationships
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-17176">
                Country relationships will be displayed here.
              </p>
            </div>
          </div>}

        {/* History Tab */}
        {activeTab === 'history' && <div className="py-4" data-id="element-17177">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-17178">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-17179">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-17180">
                  Country History
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-17181">
                  {history.length} events
                </p>
              </div>

              <div className="px-6 py-6" data-id="element-17182">
                {history.length === 0 ? <div className="text-center py-12" data-id="element-17183">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17184" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17185">
                      No history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-17186">
                      No changes have been recorded for this country yet.
                    </p>
                  </div> : <div className="flow-root" data-id="element-17187">
                    <ul className="-mb-8" data-id="element-17188">
                      {history.map((event, eventIdx) => <li key={event.id} data-id="element-17189">
                          <div className="relative pb-8" data-id="element-17190">
                            {eventIdx !== history.length - 1 && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-17191" />}
                            <div className="relative flex items-start space-x-3" data-id="element-17192">
                              <div data-id="element-17193">
                                <div className="relative px-1" data-id="element-17194">
                                  <div className="h-10 w-10 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center" data-id="element-17195">
                                    {getEventIcon(event.eventType)}
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-17196">
                                <div data-id="element-17197">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-17198">
                                    {event.eventDescription}
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-17199">
                                    {formatTimestamp(event.timestamp)}
                                  </p>
                                </div>
                                {event.changes && event.changes.length > 0 && <div className="mt-2 text-sm text-gray-700" data-id="element-17200">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-17201">
                                      {event.changes.map((change, idx) => <div key={idx} className="flex items-center text-xs" data-id="element-17202">
                                          <span className="font-medium text-gray-700" data-id="element-17203">
                                            {change.field}:
                                          </span>
                                          <span className="ml-2 text-gray-500 line-through" data-id="element-17204">
                                            {change.oldValue}
                                          </span>
                                          <span className="mx-2 text-gray-400" data-id="element-17205">
                                            →
                                          </span>
                                          <span className="text-gray-900" data-id="element-17206">
                                            {change.newValue}
                                          </span>
                                        </div>)}
                                    </div>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}